import { useSelector } from 'react-redux'
import { Concept, ConceptValues, FloorMappingEntryType } from '../../../../../../types/Survey/Analysis/Concept'
import Button from '../../../../../UI/Button/Button'
import props from '../../../../../../redux/props'
import { useState } from 'react'
import Modal from '../../../../../UI/Modal/Modal'
import { Floor } from '../../../../../../types/Survey/Survey'
import Checkbox from '../../../../../UI/Checkbox/Checkbox'
import useConceptConfiguration from '../../ConceptCalculation/useConceptConfiguration'
import FloorComparison from './FloorComparison'
import Accordion from '../../../../../UI/Accordion/Accordion'
import { Type } from '../../../../../../types/Survey/Element'
import { OrganizationUnitCooperationSelection } from '../../../../../../types/Survey/Answer/Element'
import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid'
import Tooltip from '../../../../../UI/Tooltip/Tooltip'
import { formatNumber } from '../../../../../../utility'
import _ from 'lodash'

type Props = {
  concept: Concept
}

const FloorQuickAssignment: React.FC<Props> = ({ concept }) => {
  const { updateActiveConcept, conceptDataCluster } = useConceptConfiguration()
  const t = useSelector((s) => s[props.TRANSLATION])
  const [show, setShow] = useState(false)
  const toggle = () => setShow(!show)

  const getFloors = () => conceptDataCluster.teamSurvey.context.floors
  const getUnits = () =>
    Object.keys(concept.units)
      .filter((u) => u !== 'sum')
      .map((u) => concept.units[u])
  const getUebergeordnetereBedarfe = () => concept.units.sum.UEBERGEORDNETE_BEDARFE
  const getUnitTotal = (unitUUID: string) => {
    return concept.units[unitUUID].ergebnisse.total
  }

  const getAnswer = (unitUUID: string): OrganizationUnitCooperationSelection => {
    if (!conceptDataCluster.managerSurvey || !Array.isArray(conceptDataCluster.allManagerAnswers)) return null
    const elem = conceptDataCluster.managerSurvey.elements.find((e) => e.type === Type.ORGANIZATION_UNIT_COOPERATION_SELECTION)
    if (!elem) return null
    const answer = conceptDataCluster.allManagerAnswers.find((a) => a.organizationUnitUUID === unitUUID)
    if (!answer) return null
    const answerElem = answer.elements.find((e) => e.type === Type.ORGANIZATION_UNIT_COOPERATION_SELECTION)
    if (!answerElem) return null
    return answerElem as OrganizationUnitCooperationSelection
  }

  const assignItemToFloor = (type: FloorMappingEntryType, item: any, floor: Floor) => () => {
    let floorMapping = null
    if (!concept?.floorMapping) {
      floorMapping = []
    } else {
      floorMapping = _.cloneDeep(concept.floorMapping)
    }

    floorMapping = floorMapping.filter((m) => !!m.targetUUID)

    const mapping = floorMapping.find((m) => m.type === type && m.targetUUID === (type === FloorMappingEntryType.UNIT ? item.unitUUID : item.uuid))

    if (mapping) {
      mapping.floorUUID = floor.uuid
    } else {
      if (type === FloorMappingEntryType.UNIT) {
        floorMapping.push({ type, targetUUID: item.unitUUID, floorUUID: floor.uuid })
      } else {
        floorMapping.push({ type, targetUUID: item.uuid, floorUUID: floor.uuid })
      }
    }
    updateActiveConcept({ ...concept, floorMapping })
  }

  const isSelected = (type: FloorMappingEntryType, item: any, floor: Floor) => {
    const mapping = concept.floorMapping.find(
      (mapping) =>
        mapping.type === type &&
        mapping.targetUUID === (type === FloorMappingEntryType.UNIT ? item.unitUUID : item.uuid) &&
        mapping.floorUUID === floor.uuid,
    )
    return !!mapping
  }

  const getUnitName = (unitUUID: string) => {
    const unit = getUnits().find((u) => u.unitUUID === unitUUID)
    if (!unit) return null
    return unit.name
  }

  const CooperationInfo = ({ unit }: { unit: ConceptValues }) => {
    const [show, setShow] = useState(false)
    const toggle = () => setShow(!show)
    const answer = getAnswer(unit.unitUUID)
    if (!answer) return null
    return (
      <div>
        <Tooltip text={t.survey.analysis.concept.summary.floors.quickAssignment.verortung.cta}>
          <QuestionMarkCircleIcon onClick={toggle} className="w-5 fill-cyan cursor-pointer inline-block" />
        </Tooltip>
        <Modal noForm show={show} onClose={toggle} title={t.survey.analysis.concept.summary.floors.quickAssignment.verortung.label}>
          <div className="text-gray">{t.survey.analysis.concept.summary.floors.quickAssignment.verortung.hint(unit.name)}</div>
          {answer.organizationUnitPrioSelectionList
            .sort((a, b) => a.priority - b.priority)
            .map((s, i) => {
              return (
                <div key={s.organizationUnitUUID}>
                  {`${i + 1}. `}
                  {getUnitName(s.organizationUnitUUID)}
                </div>
              )
            })}
        </Modal>
      </div>
    )
  }

  return (
    <div>
      <Button small text={t.survey.analysis.concept.summary.floors.quickAssignment.cta} onClick={toggle} />
      <Modal large noForm show={show} onClose={toggle} title={t.survey.analysis.concept.summary.floors.quickAssignment.headline}>
        <Accordion
          invert
          items={[
            {
              key: 'floorComparison',
              title: t.survey.analysis.concept.summary.floors.quickAssignment.floorComparison,
              content: <FloorComparison concept={concept} />,
            },
          ]}
        />
        <table className="border-collapse w-full mt-5">
          <thead>
            <tr>
              <th className="border">&nbsp;</th>
              {getFloors().map((floor) => (
                <th className="font-bold border-b border-r border-t px-2" key={floor.uuid}>
                  <div>{floor.name}</div>
                  <div className="text-sm font-thin">{`${formatNumber(floor.squareMeter)}${t.generic.squareMeterSuffix}`}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {getUnits().map((unit) => (
              <tr key={unit.unitUUID}>
                <td className="font-bold border-b border-r border-l px-2">
                  <div>
                    <div className="flex items-center gap-1">
                      {unit.name}
                      <CooperationInfo unit={unit} />
                    </div>
                    <div className="text-sm font-thin">{`${
                      t.UI.organizationUnitManager.productionDistance[unit.productionDistance]
                    } // ${formatNumber(getUnitTotal(unit.unitUUID))}${t.generic.squareMeterSuffix}`}</div>
                  </div>
                </td>
                {getFloors().map((floor) => (
                  <td className="border-b border-r px-2" key={floor.uuid}>
                    <div className="flex items-center justify-center w-full">
                      <Checkbox
                        width="fit"
                        checked={isSelected(FloorMappingEntryType.UNIT, unit, floor)}
                        onChange={assignItemToFloor(FloorMappingEntryType.UNIT, unit, floor)}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            {getUebergeordnetereBedarfe().map((bedarf) => (
              <tr key={bedarf.uuid}>
                <td className="font-bold border-b border-r border-l px-2">
                  <div>
                    <div>{bedarf.name}</div>
                    <div className="text-sm font-thin">{`${formatNumber(bedarf.practicalQM)}${t.generic.squareMeterSuffix}`}</div>
                  </div>
                </td>
                {getFloors().map((floor) => (
                  <td className="border-b border-r px-2" key={floor.uuid}>
                    <div className=" flex items-center justify-center w-full">
                      <Checkbox
                        width="fit"
                        checked={isSelected(FloorMappingEntryType.UEBERGEORDNETER_BEDARF, bedarf, floor)}
                        onChange={assignItemToFloor(FloorMappingEntryType.UEBERGEORDNETER_BEDARF, bedarf, floor)}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </div>
  )
}

export default FloorQuickAssignment
